/**
 * HTML Section
 * Renders raw HTML from CMS, including scripts.
 *
 * SECURITY NOTE:
 * This intentionally executes scripts embedded in CMS-provided HTML.
 * Only use with trusted content sources.
 */
"use client";

import {JSX, useEffect, useMemo, useRef} from "react";

import {type SpaceProps, useSectionSpace} from "@/sections/use-section-space";

import {Typography} from "@/shared/ui/data-display/typography";
import {ContainerBase} from "@/shared/ui/layout/container-base";

import {reinsertScripts} from "./utils";

import "./styles/html-section.scss";

export interface HtmlSectionProps {
    title?: string;
    /** Raw HTML from CMS */
    html: string;
    space?: SpaceProps;
    className?: string;
    /** Section id for in-page links (#anchor) from CMS `anchor_id` */
    anchorId?: string;
}

export function HtmlSection({title, html, space, className = "", anchorId}: HtmlSectionProps): JSX.Element {
    const sectionSpace = useSectionSpace(space);
    const rootRef = useRef<HTMLDivElement | null>(null);

    // Memoize to avoid re-decoding churn; script reinsertion handles execution on change.
    const safeHtml = useMemo(() => html ?? "", [html]);

    useEffect(() => {
        if (!rootRef.current) return;
        reinsertScripts(rootRef.current);
    }, [safeHtml]);

    return (
        <section className={`html-section ${className}`.trim()} style={sectionSpace} id={anchorId}>
            <ContainerBase>
                {anchorId ? <span className="html-section__scroll-target" id={`${anchorId}-top`} aria-hidden /> : null}
                {title ? (
                    <Typography as="h2" size={{desktop: 24, mobile: 20}} weight={600} className="html-section__title">
                        {title}
                    </Typography>
                ) : null}
                <div ref={rootRef} className="html-section__body" dangerouslySetInnerHTML={{__html: safeHtml}} />
            </ContainerBase>
        </section>
    );
}
